import axios from 'axios';
import React, { useEffect, useState } from 'react';
import { useSelector } from 'react-redux';
import ReactTable from '../../../components/ReactTable';
import { IoClose } from 'react-icons/io5';
import { FiLoader } from 'react-icons/fi';
import { CiEdit } from 'react-icons/ci';
import { GoTrash } from "react-icons/go";
import { toast } from 'react-toastify';
import { createColumnHelper } from '@tanstack/react-table';

function TDSCertificateList() {
  const { token, authToken } = useSelector((state) => state.login);
  const base = `${process.env.REACT_APP_BASE_URL}/${process.env.REACT_APP_API_TYPE}`;

  // Partner management
  const [partnerList, setPartnerList] = useState([]);
  const [selectedPartner, setSelectedPartner] = useState(null);
  const [partnerSearch, setPartnerSearch] = useState('');
  const [debouncedSearch, setDebouncedSearch] = useState('');
  const [partnerPage, setPartnerPage] = useState(1);
  const [partnerLimit, setPartnerLimit] = useState(10);
  const [partnerTotalPages, setPartnerTotalPages] = useState(1);

  // TDS Certificates
  const [tdsCertificateList, setTdsCertificateList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [searchLoading, setSearchLoading] = useState(false);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [categoryToDelete, setCategoryToDelete] = useState(null);
  const [newCategory, setNewCategory] = useState({ title: '' });
  const [editCategory, setEditCategory] = useState(null);
  const [selectedFile, setSelectedFile] = useState(null);

  const columnHelper = createColumnHelper();

  // ✅ Loader Component
  const Loader = () => (
    <div className="flex justify-center items-center py-10">
      <FiLoader className="animate-spin text-4xl text-blue-600" />
    </div>
  );

  // ✅ Fetch Partner List
  const fetchPartners = async (page = 1, limit = 10, query = '') => {
    setLoading(true);
    try {
      const response = await axios.get(`${base}/myEarnings/tdsCertificates`, {
        headers: { Authorization: `Bearer ${authToken || token}` },
        params: { page, limit, search: query },
      });
      setPartnerList(response.data?.data?.list || []);
      setPartnerTotalPages(response.data?.data?.totalPages || 1);
    } catch (error) {
      toast.error("Failed to load partners");
    } finally {
      setLoading(false);
      setSearchLoading(false);
    }
  };

  // ✅ Fetch TDS Certificates
  const fetchTdsCertificates = async (partnerId) => {
    if (!partnerId) return;
    setLoading(true);
    try {
      const response = await axios.get(`${base}/myEarnings/tdsCertificates/${partnerId}`, {
        headers: { Authorization: `Bearer ${authToken || token}` },
      });
      setTdsCertificateList(response?.data?.data?.list || []);
    } catch (error) {
      toast.error("Error fetching TDS certificates");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Create Certificate
  const handleCreateCategory = async () => {
    if (!newCategory.title) return toast.info("Please add TDS title");
    if (!selectedFile) return toast.info("Please add TDS file");
    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", newCategory.title);
      formData.append("file", selectedFile);

      await axios.post(`${base}/myEarnings/tdsCertificates/${selectedPartner.id}`, formData, {
        headers: {
          Authorization: `Bearer ${authToken || token}`,
          "Content-Type": "multipart/form-data",
        },
      });
      toast.success("TDS Certificate added");
      fetchTdsCertificates(selectedPartner.id);
      setIsModalOpen(false);
      setNewCategory({ title: "" });
      setSelectedFile(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Edit Certificate
  const handleEditCategorySubmit = async () => {
    if (!editCategory.title) return toast.info("Please add title");
    if (!selectedFile && !editCategory?.docUrl) return toast.info("Please add file");

    setLoading(true);
    try {
      const formData = new FormData();
      formData.append("title", editCategory.title);
      if (selectedFile) {
        formData.append("file", selectedFile);
      }

      await axios.put(
        `${base}/myEarnings/tdsCertificates/${selectedPartner.id}/${editCategory.id}`,
        formData,
        {
          headers: {
            Authorization: `Bearer ${authToken || token}`,
            "Content-Type": "multipart/form-data",
          },
        }
      );
      toast.success("TDS Certificate updated");
      fetchTdsCertificates(selectedPartner.id);
      setIsModalOpen(false);
      setEditCategory(null);
      setSelectedFile(null);
    } catch (error) {
      toast.error(error.response?.data?.message || "Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Delete Certificate
  const handleDeleteCategory = async () => {
    if (!categoryToDelete) return;
    setLoading(true);
    try {
      await axios.delete(
        `${base}/myEarnings/tdsCertificates/${selectedPartner.id}/${categoryToDelete}`,
        { headers: { Authorization: `Bearer ${authToken || token}` } }
      );
      toast.success("TDS Certificate deleted");
      fetchTdsCertificates(selectedPartner.id);
      setIsDeleteModalOpen(false);
      setCategoryToDelete(null);
    } catch {
      toast.error("Something went wrong");
    } finally {
      setLoading(false);
    }
  };

  // ✅ Columns for Partner List
  const partnerColumns = [
    columnHelper.accessor((row) => row.name, {
      id: 'name',
      header: 'Partner Name',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((row) => row.email, {
      id: 'email',
      header: 'Email',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((row) => row, {
      id: 'action',
      header: 'Action',
      cell: (info) => (
        <button
          onClick={() => setSelectedPartner(info.row.original)}
          className="text-blue-600 underline"
        >
          View TDS
        </button>
      ),
    }),
  ];

  // ✅ Columns for TDS List
  const tdsColumns = [
    columnHelper.accessor((row) => row.title, {
      id: 'title',
      header: 'Title',
      cell: (info) => info.getValue(),
    }),
    columnHelper.accessor((row) => row.docUrl, {
      id: 'docUrl',
      header: 'Document',
      cell: (info) =>
        info.getValue() ? (
          <a href={info.getValue()} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
            View File
          </a>
        ) : (
          "No File"
        ),
    }),
    columnHelper.accessor((row) => row, {
      id: 'action',
      header: 'Action',
      cell: (info) => {
        const item = info.row.original;
        return (
          <div className="flex justify-end gap-3">
            <span className="cursor-pointer" onClick={() => { setEditCategory(item); setIsModalOpen(true); }}>
              <CiEdit />
            </span>
            <span className="cursor-pointer" onClick={() => { setCategoryToDelete(item.id); setIsDeleteModalOpen(true); }}>
              <GoTrash />
            </span>
          </div>
        );
      },
    }),
  ];

  // ✅ Debounce logic
  useEffect(() => {
    setSearchLoading(true);
    const handler = setTimeout(() => {
      setDebouncedSearch(partnerSearch);
    }, 500); // 500ms debounce delay

    return () => {
      clearTimeout(handler);
    };
  }, [partnerSearch]);

  // ✅ Fetch partners when debounced search changes
  useEffect(() => {
    fetchPartners(partnerPage, partnerLimit, debouncedSearch);
  }, [partnerPage, partnerLimit, debouncedSearch]);

  // ✅ Fetch TDS when partner selected
  useEffect(() => {
    if (selectedPartner) fetchTdsCertificates(selectedPartner.id);
  }, [selectedPartner]);

  // ✅ Helpers
  const handleSearchChange = (e) => {
    setPartnerSearch(e.target.value);
    setPartnerPage(1);
  };

  const handleCloseModal = () => {
    setIsModalOpen(false);
    setNewCategory({ title: '' });
    setEditCategory(null);
  };

  const handleClearAll = () => {
    setEditCategory(null);
    setSelectedFile(null);
  };

  // ✅ Render
  return (
    <div className="p-6 bg-gray-50 rounded-lg mt-[50px]">
      {/* PARTNER LIST */}
      {!selectedPartner ? (
        <div>
          <div className="flex justify-between mb-4 items-center">
            <h2 className="text-2xl font-semibold text-gray-800">Partner List</h2>
            <div className="relative w-1/3">
              <input
                type="text"
                placeholder="Search partner..."
                value={partnerSearch}
                onChange={handleSearchChange}
                className="p-2 border rounded-md w-full pr-8"
              />
              {searchLoading && (
                <FiLoader className="absolute right-3 top-1/2 transform -translate-y-1/2 animate-spin text-blue-600" />
              )}
            </div>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              <ReactTable defaultData={partnerList} columns={partnerColumns} />

              {/* Pagination */}
              <div className="flex justify-between items-center mt-4">
                <div>
                  <label>Show </label>
                  <select
                    value={partnerLimit}
                    onChange={(e) => setPartnerLimit(Number(e.target.value))}
                    className="border p-1 rounded"
                  >
                    {[5, 10, 20, 50].map((num) => (
                      <option key={num} value={num}>{num}</option>
                    ))}
                  </select>
                  <label> entries</label>
                </div>
                <div className="flex items-center gap-3">
                  <button
                    disabled={partnerPage === 1}
                    onClick={() => setPartnerPage((p) => p - 1)}
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                  >
                    Prev
                  </button>
                  <span>Page {partnerPage} of {partnerTotalPages}</span>
                  <button
                    disabled={partnerPage === partnerTotalPages}
                    onClick={() => setPartnerPage((p) => p + 1)}
                    className="px-3 py-1 bg-gray-200 rounded disabled:opacity-50"
                  >
                    Next
                  </button>
                </div>
              </div>
            </>
          )}
        </div>
      ) : (
        // TDS CERTIFICATES
        <div>
          <div className="flex justify-between mb-6 items-center">
            <div className="flex items-center gap-3">
              <button className="text-blue-600 underline" onClick={() => setSelectedPartner(null)}>
                ← Back
              </button>
              <h2 className="text-2xl font-semibold text-gray-800">
                {selectedPartner.name} — TDS Certificates
              </h2>
            </div>
            <button
              onClick={() => { handleClearAll(); setIsModalOpen(true); }}
              className="bg-[#065697] text-white p-3 rounded-md transition"
            >
              Add New TDS Certificate
            </button>
          </div>

          {loading ? (
            <Loader />
          ) : (
            <>
              {tdsCertificateList?.length > 0 ? (
                <ReactTable
                  key={JSON.stringify(tdsCertificateList)}
                  defaultData={tdsCertificateList}
                  columns={tdsColumns}
                />
              ) : (
                <p className="text-gray-600">No TDS Certificates found.</p>
              )}
            </>
          )}
        </div>
      )}

      {/* ADD/EDIT MODAL */}
      {(isModalOpen || editCategory) && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <div className="flex justify-between items-center">
              <h3 className="text-xl font-semibold">
                {editCategory ? 'Edit TDS Certificate' : 'Add TDS Certificate'}
              </h3>
              <IoClose className="cursor-pointer" onClick={handleCloseModal} />
            </div>
            <div className="mt-4 space-y-4">
              <input
                type="text"
                name="title"
                value={editCategory ? editCategory.title : newCategory.title}
                onChange={(e) =>
                  editCategory
                    ? setEditCategory({ ...editCategory, title: e.target.value })
                    : setNewCategory({ ...newCategory, title: e.target.value })
                }
                placeholder="TDS Certificate Title"
                className="p-3 border rounded-md w-full"
              />

              <input
                type="file"
                accept="application/pdf"
                onChange={(e) => setSelectedFile(e.target.files[0])}
                className="p-3 border rounded-md w-full"
              />

              {selectedFile ? (
                <p className="text-sm text-gray-600">Selected: {selectedFile.name}</p>
              ) : editCategory?.docUrl ? (
                <div className="flex justify-between items-center">
                  <a href={editCategory.docUrl} target="_blank" rel="noopener noreferrer" className="text-blue-600 underline">
                    View Uploaded File
                  </a>
                  <p
                    onClick={() => setEditCategory({ ...editCategory, docUrl: '' })}
                    className="text-sm text-red-500 font-bold cursor-pointer"
                  >
                    Delete
                  </p>
                </div>
              ) : null}
            </div>

            <div className="mt-4 flex justify-end">
              <button
                onClick={editCategory ? handleEditCategorySubmit : handleCreateCategory}
                disabled={loading}
                className={`bg-[#065697] text-white p-3 rounded-md transition ${loading && 'opacity-70 cursor-not-allowed'}`}
              >
                {editCategory ? 'Update' : 'Create'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* DELETE MODAL */}
      {isDeleteModalOpen && (
        <div className="fixed inset-0 bg-gray-600 bg-opacity-50 flex justify-center items-center z-50">
          <div className="bg-white p-6 rounded-lg w-1/3">
            <h3 className="text-xl font-semibold mb-4">Are you sure you want to delete this certificate?</h3>
            <div className="flex justify-end gap-4">
              <button onClick={() => setIsDeleteModalOpen(false)} className="bg-gray-300 text-black p-2 rounded-md">
                Cancel
              </button>
              <button
                onClick={handleDeleteCategory}
                disabled={loading}
                className="bg-red-600 text-white p-2 rounded-md"
              >
                Delete
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default TDSCertificateList;
